import React, { useMemo } from "react";
import styled from "styled-components";
import { Card, Avatar, Typography } from "antd";
import { UserOutlined, MailOutlined } from "@ant-design/icons";
import COLORS from "common/Colors";
import { get, startCase } from "lodash";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";

const { Text } = Typography;

/* ===========================================================
    COLOR PALETTE (per manager column)
=========================================================== */

const PALETTE = [
  { bg: "#EFF6FF", border: "#BFDBFE", line: "#2563EB" }, // blue
  { bg: "#F0FDF4", border: "#BBF7D0", line: "#16A34A" }, // green
  { bg: "#FFFBEB", border: "#FDE68A", line: "#D97706" }, // amber
  { bg: "#F5F3FF", border: "#DDD6FE", line: "#7C3AED" }, // purple
  { bg: "#FDF2F8", border: "#FBCFE8", line: "#DB2777" }, // pink
];

const LINE_COLOR = "#2563EB";

/* ===========================================================
    LAYOUT PRIMITIVES
=========================================================== */

const Wrapper = styled.div`
  width: 100%;
  background: #f6f8fb;
  padding: 40px 24px;
  overflow-x: auto;
`;

const ChartContainer = styled.div`
  min-width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const CEOSection = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const CEOStem = styled.div`
  width: 2px;
  height: 36px;
  margin-left: 149px; /* centers under the 300px-wide CEO card */
  background: ${LINE_COLOR};
`;

const ManagersRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 48px;
  position: relative;
`;

const ManagerColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  min-width: 260px;
  max-width: 300px;
  padding-top: 36px;

  /* vertical stem dropping to this manager */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    width: 2px;
    height: 36px;
    background: ${LINE_COLOR};
  }

  /* horizontal segment of the shared branch line -
     extends past the column edge by half the row gap so it
     bridges seamlessly into the next/previous column's segment */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    height: 2px;
    background: ${LINE_COLOR};
    left: ${(props) => (props.$isFirst ? "50%" : "-24px")};
    right: ${(props) => (props.$isLast ? "50%" : "-24px")};
    display: ${(props) => (props.$isOnly ? "none" : "block")};
  }
`;

const BranchDot = styled.div`
  position: absolute;
  top: -5px;
  left: calc(50% - 5px);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${LINE_COLOR};
`;

const ManagerStem = styled.div`
  width: 2px;
  height: 28px;
  background: ${LINE_COLOR};
`;

const EmployeeContainer = styled.div`
  width: 100%;
  margin-top: -1px;
  padding: 20px 16px;
  border-radius: 16px;
  background: ${(props) => props.$bg};
  border: 2px dashed ${(props) => props.$border};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

/* ===========================================================
    USER CARD
=========================================================== */

const CardWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const LevelBadge = styled.div`
  position: absolute;
  top: -10px;
  left: -10px;
  width: 32px;
  height: 32px;
  background: #2563eb;
  color: #ffffff;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  font-size: 12px;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.35);
`;

const StyledCard = styled(Card)`
  width: 100%;
  border-radius: 14px;
  border: 1px solid ${(props) => props.$border || "#eef1f6"};
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);

  .ant-card-body {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1; /* NEW - Details absorbs the extra/limited space instead */
  min-width: 0; /* keep this so long text can still wrap/ellipsis */
`;

const Name = styled(Text)`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Designation = styled(Text)`
  font-size: 13px;
  color: #6b7280;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 12px;
  color: #2563eb;

  .anticon {
    color: #2563eb;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
const StyledAvatar = styled(Avatar)`
  font-size: 2rem;
  height: 60px;
  width: 60px;
  flex-shrink: 0; /* NEW - avatar can never be compressed by flex */
  flex-grow: 0;
  background-color: ${COLORS.PRIMARY};
  vertical-align: middle;
  .ant-avatar-string {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const UserCard = ({ user, level, accent }) => {
  return (
    <CardWrapper>
      <LevelBadge>{level}</LevelBadge>

      <StyledCard $border={accent}>
        <Header>
          {user?.profileurl === null ||
          user?.profileurl === "" ||
          Array.isArray(user?.profileurl) ? (
            <StyledAvatar size="default">
              {startCase(user?.fullname).charAt(0)}
            </StyledAvatar>
          ) : (
            <StyledAvatar src={user?.profileurl} size="default" />
          )}
          {/* <StyledAvatar
            size={56}
            src={user?.profileurl}
            icon={<UserOutlined />}
          /> */}

          <Details>
            <Name>{user?.fullname || "Unknown"}</Name>
            <Designation>{user?.jobtitle || "-"}</Designation>
          </Details>
        </Header>

        <EmailRow>
          <MailOutlined />
          <span>{user?.email || user?.userid}</span>
        </EmailRow>
      </StyledCard>
    </CardWrapper>
  );
};

/* ===========================================================
    MAIN COMPONENT
=========================================================== */

export default function OrganizationalChart() {
  /* ==========================================
      FIND CEO
  ========================================== */
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);
  const HigherOfficialsList = userInfo?.higherOfficialsList;
  const ceo = useMemo(() => {
    const ceoOfficial = HigherOfficialsList.find(
      (item) => item.role?.toUpperCase() === "CEO"
    );

    if (!ceoOfficial) return null;

    return userList.find((user) => {
      const userId = (user.userid || "").toLowerCase().trim();
      const email = (user.email || "").toLowerCase().trim();
      const ceoEmail = (ceoOfficial.email || "").toLowerCase().trim();

      return userId === ceoEmail || email === ceoEmail;
    });
  }, [HigherOfficialsList, userList]);

  /* ==========================================
      REPORTING MANAGERS (deduped, in order of first appearance)
  ========================================== */

  const managers = useMemo(() => {
    const managerMap = {};

    userList.forEach((employee) => {
      if (!employee.reportingmanager) return;

      const manager = userList.find((user) => {
        const id = (user.userid || "").toLowerCase();
        const email = (user.email || "").toLowerCase();
        const reporting = employee.reportingmanager.toLowerCase();

        return id === reporting || email === reporting;
      });

      if (!manager) return;

      if (!managerMap[manager.userid]) {
        managerMap[manager.userid] = manager;
      }
    });

    return Object.values(managerMap);
  }, [userList]);

  /* ==========================================
      GROUP EMPLOYEES UNDER EACH MANAGER
  ========================================== */

  const managerWiseEmployees = useMemo(() => {
    const obj = {};

    managers.forEach((manager) => {
      obj[manager.userid] = userList.filter((employee) => {
        return (
          employee.reportingmanager &&
          employee.reportingmanager.toLowerCase() ===
            manager.userid.toLowerCase()
        );
      });
    });

    return obj;
  }, [managers, userList]);

  return (
    <Wrapper>
      <ChartContainer>
        {/* Level 1 - CEO */}
        <CEOSection>
          <div style={{ width: 300 }}>
            <UserCard user={ceo} level="L1" />
          </div>
        </CEOSection>

        <CEOStem />

        {/* Level 2 & 3 - Managers + their employees */}
        <ManagersRow>
          {managers.map((manager, index) => {
            const isFirst = index === 0;
            const isLast = index === managers.length - 1;
            const isOnly = managers.length === 1;
            const colors = PALETTE[index % PALETTE.length];
            const employees = managerWiseEmployees[manager.userid] || [];

            return (
              <ManagerColumn
                key={manager.userid}
                $isFirst={isFirst}
                $isLast={isLast}
                $isOnly={isOnly}
              >
                <BranchDot />

                <UserCard level="L2" user={manager} accent={colors.border} />

                {employees.length > 0 && (
                  <>
                    <ManagerStem />
                    <EmployeeContainer $bg={colors.bg} $border={colors.border}>
                      {employees.map((emp) => (
                        <UserCard
                          key={emp.userid}
                          level="L3"
                          user={emp}
                          accent={colors.border}
                        />
                      ))}
                    </EmployeeContainer>
                  </>
                )}
              </ManagerColumn>
            );
          })}
        </ManagersRow>
      </ChartContainer>
    </Wrapper>
  );
}
